import React from 'react';
import PropTypes from 'prop-types';
import useMountedGuard from 'Hooks/useMountedGuard';

const RightSidebarContext = React.createContext(undefined);

// Actions
const CLEAR_RIGHT_SIDEBAR_STATE = 'clear-right-sidebar-state';
const START_LOADING_RIGHT_SIDEBAR_COLLECTION = 'start-loading-right-sidebar-collection';
const FAILED_LOADING_RIGHT_SIDEBAR_COLLECTION = 'failed-loading-right-sidebar-collection';
const SET_RIGHT_SIDEBAR_COLLECTION = 'set-right-sidebar-collection';

const init = () => {
  return {
    rightSidebarCollectionLoading: false,
    rightSidebarCollectionError: null,
    rightSidebarCollection: null,
  };
};

const reducer = (state, action) => {
  switch (action.type) {
    case CLEAR_RIGHT_SIDEBAR_STATE: {
      return init();
    }
    case START_LOADING_RIGHT_SIDEBAR_COLLECTION: {
      return {
        ...state,
        rightSidebarCollectionLoading: true,
      };
    }
    case FAILED_LOADING_RIGHT_SIDEBAR_COLLECTION: {
      return {
        ...state,
        rightSidebarCollectionError: action.rightSidebarCollectionError,
      };
    }
    case SET_RIGHT_SIDEBAR_COLLECTION: {
      return {
        ...state,
        rightSidebarCollectionLoading: false,
        rightSidebarCollectionError: null,
        rightSidebarCollection: action.rightSidebarCollection,
      };
    }
    default: {
      throw new Error(`Unhandled RightSidebarContext action type: ${action.type}`);
    }
  }
};

const RightSidebarProvider = ({
  children,
}) => {
  const [state, _dispatch] = React.useReducer(reducer, null, init);
  const { doWithMountedGuard } = useMountedGuard();
  const dispatch = doWithMountedGuard(_dispatch);

  const clearRightSidebarCollection = () => {
    dispatch({ type: CLEAR_RIGHT_SIDEBAR_STATE });
  };

  const setRightSidebarCollection = (coll) => {
    if (coll.resources().length() === 0) {
      coll.create({
        addToCollection: true,
      });
    }

    dispatch({ type: SET_RIGHT_SIDEBAR_COLLECTION, rightSidebarCollection: coll });
  };

  const loadRightSidebarCollectionLink = async (link) => {
    dispatch({ type: START_LOADING_RIGHT_SIDEBAR_COLLECTION });
    try {
      const resp = await link.get();
      setRightSidebarCollection(resp);
    } catch (err) {
      dispatch({ type: FAILED_LOADING_RIGHT_SIDEBAR_COLLECTION, rightSidebarCollectionError: err });
    }
  };

  const memoizedValue = React.useMemo(() => {
    return {
      showRightSidebarCollection: state.rightSidebarCollection || state.rightSidebarCollectionLoading || state.rightSidebarCollectionError,
      rightSidebarCollection: state.rightSidebarCollection,
      rightSidebarFirstResource: state.rightSidebarCollection?.resources().head(),
      rightSidebarCollectionError: state.rightSidebarCollectionError,
      rightSidebarCollectionLoading: state.rightSidebarCollectionLoading,
      loadRightSidebarCollectionLink,
      clearRightSidebarCollection,
      setRightSidebarCollection,
    };
  }, [
    state,
    loadRightSidebarCollectionLink,
    clearRightSidebarCollection,
    setRightSidebarCollection,
  ]);

  return (
    <RightSidebarContext.Provider value={memoizedValue}>
      {children}
    </RightSidebarContext.Provider>
  );
};

// Use RightSidebar State Context
const useRightSidebarContext = () => {
  const context = React.useContext(RightSidebarContext);
  if (context === undefined) {
    throw new Error('useRightSidebarContext must be used within a RightSidebarProvider');
  }
  return context;
};

RightSidebarProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
};

export {
  RightSidebarProvider,
  useRightSidebarContext,
};
